import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PercentageInput, PercentageInputProps } from '../../UI';
import { contributionPercentageToPersist } from '../contributionPairs';
import { useContributionFieldValues } from '../hooks/useContributionFieldValues';
import { useManualContributionEntered } from '../hooks/useManualContributionEntered';
import { InvestmentFormValues } from '../types';

type InvestmentRecurringContributionPercentageInputProps = Pick<PercentageInputProps, 'label' | 'className'>;

export const InvestmentRecurringContributionPercentageInput = ({
  label,
  className,
}: InvestmentRecurringContributionPercentageInputProps) => {
  const { t: tForm } = useTranslation('form');
  const { control, setValue, clearErrors } = useFormContext<InvestmentFormValues>();
  // Disabled only once the manual side (amount) is in use — a blank form leaves it editable.
  const manualContributionEntered = useManualContributionEntered();
  const { incomeSourceID, percentage: watchedPercentage } = useContributionFieldValues();
  // A linked source with no percentage is a valid "not contributing" state (ELE-1121); say so,
  // since the amount/frequency stay blank and locked.
  const notContributing = contributionPercentageToPersist(watchedPercentage, incomeSourceID) === 0;
  const { field, fieldState } = useController<InvestmentFormValues, 'recurringContributionPercentage'>({
    control,
    name: 'recurringContributionPercentage',
    rules: {
      // 0/blank means "no percentage"; only a real out-of-range value is a range error (the
      // input already caps entry at 0–100, so this is a backstop).
      validate: (percentage) =>
        percentage === null || percentage === undefined || (percentage >= 0 && percentage <= 100),
    },
  });

  return (
    <PercentageInput
      {...field}
      onChange={(percentage) => {
        field.onChange(percentage);
        // Clearing the percentage (or entering 0) stops the contribution: the derived
        // amount/frequency are blanked and the income source stays linked.
        if (percentage === null || percentage === 0) {
          setValue('recurringContribution.amount', null);
          setValue('recurringContribution.frequency', null);
        }
        // Clear standing contribution errors as the user edits — they re-check on the next save
        // rather than nagging live (validation is submit-only, see useInvestmentForm).
        clearErrors([
          'incomeSourceID',
          'recurringContributionPercentage',
          'recurringContribution.amount',
          'recurringContribution.frequency',
        ]);
      }}
      className={className}
      inputProps={{ readOnly: manualContributionEntered }}
      status={manualContributionEntered ? 'disabled' : fieldState.error ? 'errored' : 'normal'}
      helperText={
        fieldState.error?.message ??
        (notContributing ? tForm('investment-percentage-not-contributing-helper') : undefined)
      }
      label={label ?? tForm('investment-recurring-contribution-percentage-input-label')}
    />
  );
};
